import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Typography, Input, Checkbox, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import AddCircleIcon from "../../assets/svg/AddCircleIcon";
import styled, { keyframes } from "styled-components";

import { WHITE, GRAY_500, BLACK, WARNING_600 } from "../../constant/colors";
import { SettingsIcon } from "../../assets/svg/SettingsIcon";
import PhysicianIcon from "../../assets/svg/PhysicianIcon";
import ACTIONS from "../../store/action";
import {
  selectProvidersAllDoctors,
  selectUser,
  selectEnvironment,
  selectUsers,
  selectPhysicianListFilters
} from "../../store/selector";
import { trackPendoEvent } from "../../utils/pendo/pendoUtils";
import EmptyPhysicianListIcon from "../../assets/svg/EmptyPhysicianListIcon";
import ExportIcon from "../../assets/svg/ExportIcon";
import LoadingSpinner from "../../assets/svg/LoadingSpinner";

import { PhysicianList } from "./PhysicianList";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import {
  BreadcrumbSection,
  BreadcrumbItem,
  BreadcrumbSeparator,
  BreadcrumbCurrent,
  ButtonRow,
  StyledSecondaryButton,
  StyledPrimaryButton
} from "../../component/sharedComponents/settingsEntity/components";
import { handleSettingsClick as sharedHandleSettingsClick } from "./shared/handlers";
import { getUserName } from "../../component/sharedComponents/settingsEntity/utils";
import { dateTimeFormatter } from "../../utils/date";
import { fontWeights, sizes } from "../../constant/styles";

const { Text } = Typography;

const spinClockwise = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const SpinnerWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  animation: ${spinClockwise} 1s linear infinite;
`;

const PhysicianManagementContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderSection = styled.div`
  flex-shrink: 0;
  padding: 8px 4px;
`;

const ListBreadcrumbSection = styled(BreadcrumbSection)`
  justify-content: flex-start;
`;

const ContentHeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  min-height: 100%;
`;

const PageTitle = styled.div`
  font-family: "Open Sans";
  font-size: ${sizes.medium_xlarge};
  font-weight: 700;
  color: #000000;
`;

const PageDescription = styled(Text)`
  font-size: 14px;
  font-weight: ${fontWeights.regular};
  color: ${BLACK};
  display: block;
`;

const SearchInput = styled(Input)`
  width: 263px;
  height: 40px;
  border-radius: 10px;

  .ant-input {
    border-radius: 10px;
  }

  &.ant-input-affix-wrapper {
    border-radius: 10px;
    padding: 8px 16px;
  }

  &.ant-input-affix-wrapper:hover {
    border-color: ${BLACK} !important;
  }
`;

const ContentSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  cursor: default;
`;

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  svg {
    width: 64px;
    height: 64px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-family: "Open Sans";
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-family: "Open Sans";
  font-size: 14px;
  color: ${GRAY_500};
  margin: 0 0 24px 0;
`;

const EmptyStateButton = styled(StyledPrimaryButton)`
  margin-bottom: 16px;
`;

const EmptyStateBulkText = styled.p`
  font-family: "Open Sans";
  font-size: 12px;
  font-weight: 400;
  line-height: 150%;
  color: ${BLACK};
  text-align: center;
  margin: 0;

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PhysicianManagement = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const users = useSelector(selectUsers) || {};

  const physicians = useSelector(selectProvidersAllDoctors) || [];
  const physicianListFilters = useSelector(selectPhysicianListFilters);

  const searchTerm = physicianListFilters.searchTerm;
  const showInactive = physicianListFilters.showInactive;
  const currentPage = physicianListFilters.currentPage;
  const sortColumn = physicianListFilters.sortColumn;
  const sortOrder = physicianListFilters.sortOrder;
  const [isExporting, setIsExporting] = useState(false);
  const exportButtonRef = useRef(null);

  const setSearchTerm = (value) => {
    dispatch(ACTIONS.setPhysicianListFilters({ searchTerm: value }));
  };

  const setShowInactive = (value) => {
    dispatch(ACTIONS.setPhysicianListFilters({ showInactive: value }));
  };

  const setCurrentPage = (value) => {
    dispatch(ACTIONS.setPhysicianListFilters({ currentPage: value }));
  };

  const setSorting = (column, order) => {
    dispatch(ACTIONS.setPhysicianListFilters({ sortColumn: column, sortOrder: order }));
  };

  // Load physicians on mount and reset all filters to defaults
  useEffect(() => {
    dispatch(ACTIONS.providersAction("doctor"));
    dispatch(
      ACTIONS.setPhysicianListFilters({
        searchTerm: "",
        showInactive: false,
        currentPage: 1,
        sortColumn: null,
        sortOrder: null
      })
    );
  }, [dispatch]);

  // Filter and sort physicians based on search, active status, and sort settings
  const filteredPhysicians = useMemo(() => {
    let filtered = physicians;

    // Filter by active status
    if (!showInactive) {
      filtered = filtered.filter((physician) => physician.isActive !== false);
    }

    // Filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((physician) => {
        return (
          physician.name?.toLowerCase().includes(lowerSearch) ||
          physician.npi?.toLowerCase().includes(lowerSearch) ||
          physician.email?.toLowerCase().includes(lowerSearch) ||
          physician.facility?.toLowerCase().includes(lowerSearch) ||
          physician.city?.toLowerCase().includes(lowerSearch) ||
          physician.state?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    // Apply sorting
    if (sortColumn && sortOrder) {
      filtered = [...filtered].sort((a, b) => {
        let comparison = 0;
        if (sortColumn === "nameId") {
          comparison = (a.npi || "").localeCompare(b.npi || "");
        } else if (sortColumn === "isActive") {
          // Sort active first, then inactive
          if (a.isActive === b.isActive) {
            comparison = 0;
          } else {
            comparison = a.isActive ? -1 : 1;
          }
        }
        // Reverse for descending order
        return sortOrder === "descend" ? -comparison : comparison;
      });
    }

    return filtered;
  }, [physicians, searchTerm, showInactive, sortColumn, sortOrder]);

  // Check if there are both active and inactive physicians (for conditional status sorting)
  const hasBothActiveAndInactive = useMemo(() => {
    const hasActive = filteredPhysicians.some((p) => p.isActive !== false);
    const hasInactive = filteredPhysicians.some((p) => p.isActive === false);
    return hasActive && hasInactive;
  }, [filteredPhysicians]);

  const handleSettingsClick = () => sharedHandleSettingsClick({ dispatch, user, account, history });

  const handleSearchFocus = () => {
    try {
      trackPendoEvent(dispatch, "Physicians_Search", {
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
  };

  const handleShowInactiveChange = (e) => {
    const isChecked = e.target.checked;
    try {
      trackPendoEvent(dispatch, "Physician_Show_Inactive_Filter", {
        username: user?.email,
        account,
        showInactive: isChecked
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    setShowInactive(isChecked);
  };

  const handleAddPhysician = () => {
    try {
      trackPendoEvent(dispatch, "Physicians_AddPhysician", {
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    history.push("/physicians-settings/new");
  };

  const handleAddPhysicianFromEmptyState = () => {
    try {
      trackPendoEvent(dispatch, "Physician_NoPhysicians_AddButton", {
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    history.push("/physicians-settings/new");
  };

  const handleContactSupportFromEmptyState = () => {
    try {
      trackPendoEvent(dispatch, "Physician_NoPhysicians_ContactSupport", {
        username: user?.email,
        account
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
  };

  const handleAddPhysicianFromSearchNoResults = () => {
    try {
      trackPendoEvent(dispatch, "Physician_SearchResults_NoPhysicians_AddPhysician", {
        username: user?.email,
        account,
        searchTerm
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    history.push("/physicians-settings/new");
  };

  const handleEditPhysician = (physician) => {
    try {
      trackPendoEvent(dispatch, "Physician_MainView_Edit", {
        username: user?.email,
        account,
        physicianId: physician.id
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    history.push(`/physicians-settings/${physician.id}/edit`);
  };

  const handleViewPhysician = (physician) => {
    try {
      trackPendoEvent(dispatch, "Physician_Row_ClickToView", {
        username: user?.email,
        account,
        physicianId: physician.id
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }
    history.push(`/physicians-settings/${physician.id}`);
  };

  const handleExportCSV = () => {
    setIsExporting(true);

    try {
      trackPendoEvent(dispatch, "Physicians_DownloadCSV", {
        username: user?.email,
        account,
        physicianCount: filteredPhysicians.length
      });
    } catch (error) {
      console.error("Error dispatching Pendo event:", error);
    }

    // Defer export so React can render the loading state, with minimum 500ms spinner visibility
    const startTime = Date.now();
    setTimeout(() => {
      try {
        // Helper function to escape CSV values
        const escapeCsvValue = (value) => {
          if (value === null || value === undefined) return "";
          const stringValue = String(value);
          // Escape quotes by doubling them
          return stringValue.replace(/"/g, '""');
        };

        // CSV export logic
        const headers = [
          t("physician_management.list.csv_header_id"),
          t("physician_management.list.csv_header_name"),
          t("physician_management.list.csv_header_title"),
          t("physician_management.list.csv_header_facility"),
          t("physician_management.list.csv_header_phone"),
          t("physician_management.list.csv_header_fax"),
          t("physician_management.list.csv_header_address"),
          t("physician_management.list.csv_header_suite"),
          t("physician_management.list.csv_header_city"),
          t("physician_management.list.csv_header_state"),
          t("physician_management.list.csv_header_zip"),
          t("physician_management.list.csv_header_npi"),
          t("physician_management.list.csv_header_email"),
          t("physician_management.list.csv_header_ptan"),
          t("physician_management.list.csv_header_dea"),
          t("physician_management.list.csv_header_license"),
          t("physician_management.list.csv_header_taxid"),
          t("physician_management.list.csv_header_signature"),
          t("physician_management.list.csv_header_active"),
          t("physician_management.list.csv_header_created_at"),
          t("physician_management.list.csv_header_created_by"),
          t("physician_management.list.csv_header_updated_at"),
          t("physician_management.list.csv_header_updated_by")
        ];
        const csvContent = [
          headers.map((h) => `"${escapeCsvValue(h)}"`).join(","),
          ...filteredPhysicians.map((physician) => {
            return [
              `"${escapeCsvValue(physician.id)}"`,
              `"${escapeCsvValue(physician.name)}"`,
              `"${escapeCsvValue(physician.title)}"`,
              `"${escapeCsvValue(physician.facility)}"`,
              `"${escapeCsvValue(physician.phone)}"`,
              `"${escapeCsvValue(physician.fax)}"`,
              `"${escapeCsvValue(physician.address)}"`,
              `"${escapeCsvValue(physician.suiteNumber)}"`,
              `"${escapeCsvValue(physician.city)}"`,
              `"${escapeCsvValue(physician.state)}"`,
              `"${escapeCsvValue(physician.zipCode)}"`,
              `"${escapeCsvValue(physician.npi)}"`,
              `"${escapeCsvValue(physician.email)}"`,
              `"${escapeCsvValue(physician.ptan)}"`,
              `"${escapeCsvValue(physician.dea)}"`,
              `"${escapeCsvValue(physician.stateLicense)}"`,
              `"${escapeCsvValue(physician.taxId)}"`,
              `"${escapeCsvValue(physician.signatureFileName)}"`,
              `"${escapeCsvValue(physician.isActive)}"`,
              `"${escapeCsvValue(dateTimeFormatter(physician.createdAt))}"`,
              `"${escapeCsvValue(
                getUserName(users, physician.createdBy, t("physician_management.detail.default_user"))
              )}"`,
              `"${escapeCsvValue(dateTimeFormatter(physician.userUpdatedAt))}"`,
              `"${escapeCsvValue(
                getUserName(users, physician.updatedBy, t("physician_management.detail.default_user"))
              )}"`
            ].join(",");
          })
        ].join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        const now = new Date();
        const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
          now.getDate()
        ).padStart(2, "0")}`;
        let hours = now.getHours();
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        const timeStr = `${hours}-${String(now.getMinutes()).padStart(2, "0")}${ampm}`;
        link.setAttribute("download", `physicians_${dateStr}_${timeStr}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Show success message
        SuccessMessage(t("physician_management.list.export_success"));
      } catch (error) {
        console.error("Error exporting CSV:", error);
        message.error(t("physician_management.list.export_error"));
      } finally {
        // Ensure spinner shows for at least 500ms
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(500 - elapsed, 0);
        setTimeout(() => {
          setIsExporting(false);
          if (exportButtonRef.current) {
            exportButtonRef.current.blur();
          }
        }, remaining);
      }
    }, 0);
  };

  return (
    <PhysicianManagementContainer>
      <HeaderSection>
        <ListBreadcrumbSection>
          <BreadcrumbItem id="Physician_Breadcrumbs_Settings" onClick={handleSettingsClick}>
            <SettingsIcon />
            {t("physician_management.breadcrumb.settings")}
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbCurrent>
            <PhysicianIcon />
            {t("physician_management.breadcrumb.physicians")}
          </BreadcrumbCurrent>
        </ListBreadcrumbSection>
      </HeaderSection>

      <ContentHeaderSection>
        <LeftColumn>
          <PageTitle>{t("physician_management.list.page_title")}</PageTitle>
          <PageDescription>{t("physician_management.list.page_description")}</PageDescription>
          {physicians.length > 0 && (filteredPhysicians.length > 0 || !searchTerm) && (
            <Checkbox
              id="Physician_Show_Inactive_Filter"
              checked={showInactive}
              onChange={handleShowInactiveChange}
              style={{ marginTop: "8px" }}
            >
              {t("physician_management.list.show_inactive")}
            </Checkbox>
          )}
        </LeftColumn>
        <RightColumn>
          {physicians.length > 0 && (
            <SearchInput
              id="Physicians_Search"
              placeholder={t("physician_management.list.search_placeholder")}
              suffix={
                <SearchOutlined
                  style={{ color: searchTerm && filteredPhysicians.length === 0 ? WARNING_600 : undefined }}
                />
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={handleSearchFocus}
              allowClear
            />
          )}
          <div style={{ marginTop: physicians.length > 0 ? "auto" : "0", paddingTop: "24px" }}>
            <ButtonRow>
              <StyledSecondaryButton
                id="Physicians_DownloadCSV"
                ref={exportButtonRef}
                icon={
                  isExporting ? (
                    <SpinnerWrapper>
                      <LoadingSpinner />
                    </SpinnerWrapper>
                  ) : (
                    <ExportIcon
                      style={{
                        fill: filteredPhysicians.length === 0 ? WHITE : BLACK,
                        stroke: filteredPhysicians.length === 0 ? WHITE : BLACK
                      }}
                    />
                  )
                }
                onClick={handleExportCSV}
                disabled={filteredPhysicians.length === 0}
              >
                {isExporting ? t("physician_management.list.exporting") : t("physician_management.list.export")}
              </StyledSecondaryButton>
              <StyledPrimaryButton
                id="Physicians_AddPhysician"
                type="primary"
                icon={<AddCircleIcon />}
                onClick={handleAddPhysician}
              >
                {t("physician_management.list.add_physician")}
              </StyledPrimaryButton>
            </ButtonRow>
          </div>
        </RightColumn>
      </ContentHeaderSection>

      <ContentSection>
        {physicians.length === 0 ? (
          <EmptyStateContainer>
            <EmptyStateIcon>
              <EmptyPhysicianListIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>{t("physician_management.list.empty_state_title")}</EmptyStateTitle>
            <EmptyStateDescription>{t("physician_management.list.empty_state_description")}</EmptyStateDescription>
            <EmptyStateButton
              id="Physician_NoPhysicians_AddButton"
              type="primary"
              icon={<AddCircleIcon />}
              onClick={handleAddPhysicianFromEmptyState}
            >
              {t("physician_management.list.add_physician")}
            </EmptyStateButton>
            <EmptyStateBulkText>
              {t("physician_management.list.empty_state_bulk_upload_text")}{" "}
              <a
                id="Physician_NoPhysicians_ContactSupport"
                href="mailto:support@tailormed.co"
                onClick={handleContactSupportFromEmptyState}
              >
                {t("physician_management.list.empty_state_contact_support")}
              </a>{" "}
              {t("physician_management.list.empty_state_bulk_upload_suffix")}
            </EmptyStateBulkText>
          </EmptyStateContainer>
        ) : (
          <PhysicianList
            physicians={filteredPhysicians}
            totalPhysicianCount={physicians.length}
            onEdit={handleEditPhysician}
            onViewDetails={handleViewPhysician}
            onAddPhysician={handleAddPhysicianFromSearchNoResults}
            isSearchResult={searchTerm.length > 0 || showInactive}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSortChange={setSorting}
            hasBothActiveAndInactive={hasBothActiveAndInactive}
          />
        )}
      </ContentSection>
    </PhysicianManagementContainer>
  );
};

export { PhysicianManagement };
